import type { FC, ReactNode } from 'react'
import type { Theme } from '@mui/material'
import { alpha, useTheme } from '@mui/material'
import type { ClaudeSession } from '@/features/events/conversation/state/types'
import {
  getDisplaySessionName,
  isNotInteractable,
} from '@/features/events/conversation/state/types'
import type {
  TreeNodeStyleConfig,
  ContentContainerConfig,
  SessionIconRenderer,
} from '../types/SessionTreeProps'
import { SessionNode } from '../SessionNode'

export interface SessionNodeRendererProps {
  readonly session: ClaudeSession
  readonly iconConfig: ReactNode | SessionIconRenderer | null | undefined
  readonly styleConfig: TreeNodeStyleConfig
  readonly contentContainerConfig: ContentContainerConfig
  readonly expandPosition: 'left' | 'right'
  readonly isExpanded: boolean
  readonly onToggle: () => void
  readonly actions?: ReactNode[] | undefined
  readonly persistentActions?: ReactNode[] | undefined
  readonly renderOrchestrator: (session: ClaudeSession, isExpanded: boolean) => ReactNode
}

export const SessionNodeRenderer: FC<SessionNodeRendererProps> = ({
  session,
  iconConfig,
  styleConfig,
  contentContainerConfig,
  expandPosition,
  isExpanded,
  onToggle,
  actions,
  persistentActions,
  renderOrchestrator,
}) => {
  const theme = useTheme()
  const resolvedIcon = resolveSessionIcon(iconConfig, session)
  const borderColor =
    contentContainerConfig.borderColor && contentContainerConfig.borderColor !== 'divider'
      ? contentContainerConfig.borderColor
      : resolveSessionBorderColor(session, theme)

  const resolvedContentContainerConfig = {
    ...contentContainerConfig,
    borderColor,
  }

  return (
    <SessionNode
      session={session}
      label={getDisplaySessionName(session)}
      icon={resolvedIcon}
      styleConfig={styleConfig}
      {...(actions && { actions })}
      {...(persistentActions && { persistentActions })}
      isExpanded={isExpanded}
      onToggle={onToggle}
      expandPosition={expandPosition}
      contentContainerConfig={resolvedContentContainerConfig}
    >
      {renderOrchestrator(session, isExpanded)}
    </SessionNode>
  )
}

function resolveSessionIcon(
  iconConfig: ReactNode | SessionIconRenderer | null | undefined,
  session: ClaudeSession,
): ReactNode | null {
  if (iconConfig === null || iconConfig === undefined) {
    return null
  }
  if (typeof iconConfig === 'function') {
    return iconConfig(session)
  }
  return iconConfig
}

export function resolveSessionBorderColor(session: ClaudeSession, theme: Theme): string {
  if (isNotInteractable(session)) {
    return alpha(theme.palette.warning.main, 0.3)
  }
  if (session.dangerouslySkipPermissions) {
    return alpha(theme.palette.error.main, 0.3)
  }
  return alpha(theme.palette.success.main, 0.3)
}
